import React, { lazy, Suspense } from 'react';

/**
 * Type for component modules that can be lazy-loaded
 */
type LazyModule<T> = () => Promise<{ [K in keyof T]: React.ComponentType<any> }>;

/**
 * Create lazy-loaded components with TypeScript support
 * @param factory Function that imports the module
 * @returns Object with the same keys but components are lazy-loaded
 */
export function lazily<T extends Record<string, React.ComponentType<any>>>(
  factory: LazyModule<T>
): T {
  const lazyComponents: Partial<T> = {};
  
  // Create a proxy that intercepts property access
  return new Proxy(lazyComponents as T, {
    get: (target, componentName: string | symbol) => {
      const name = componentName.toString();
      
      if (!target[name as keyof T]) {
        // Create lazy component on first access
        target[name as keyof T] = lazy(async () => {
          const module = await factory();
          if (!module[name as keyof T]) {
            throw new Error(`Component ${name} not found in module`);
          }
          return { default: module[name as keyof T] };
        }) as T[keyof T];
      }
      
      // Wrap in Suspense with fallback
      const LazyComponent = target[name as keyof T]!;
      return (props: any) => (
        <Suspense fallback={<LoadingFallback />}>
          <LazyComponent {...props} />
        </Suspense>
      );
    }
  });
}

/**
 * Simple loading fallback for lazy-loaded components
 */
function LoadingFallback() {
  return (
    <div className="flex items-center justify-center p-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6563ff]"></div>
    </div>
  );
}
